<template>
  <div>
    <!-- Modalità automatica -->
    <template v-if="mode === 'auto'">
      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','First photo price')"
             :help-text="$pgettext('priceList.pack.help', 'Set the selling price of the first photo')">
        <PCurrencyInput v-model="configuration.first_photo_price" :currency="currency"/>
      </PFRow>

      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','Complete package')"
             :help-text="$pgettext('priceList.pack.help', 'Choose how many photos make a complete package and its price')"
      >
        <CRow>
          <CCol sm="3">
            <label class="">{{ $pgettext('priceList.pack.label', 'N° photos') }}</label>
            <PSelectKeyValue placeholder="" :options="setNums" :value="configuration.complete_set_num" @input="onSelectNum"/>
          </CCol>
          <CCol sm="9">
            <label class="">{{ $pgettext('priceList.pack.label', 'Package price') }}</label>
            <PCurrencyInput v-model="configuration.complete_set_price" :currency="currency"/>
          </CCol>
        </CRow>
      </PFRow>

      <PAlert v-if="isFirstPhotoPriceTooLow" color="warning">
        {{
          $pgettext('priceList.pack.validation', 'The first photo price must be higher than the average per-photo price of the complete package, otherwise the per-photo price would increase with quantity.')
        }}
      </PAlert>

    </template>

    <!-- Modalità manuale -->
    <template v-if="mode === 'manual'">
      <CRow>
        <CCol lg="6">
          <h6 class="font-weight-semi-bold mt-3">
            {{ $pgettext('priceList.pack.manual', 'Complete price list') }}
          </h6>
          <p class="text-help-dark small">
            {{
              $pgettext('priceList.pack.manual', 'The price list has been applied based on your choices. You can modify the prices generated by our algorithm.')
            }}
          </p>
        </CCol>
        <CCol sm="12" :lg="{size: '4', offset:'2' }">
          <CDataTable
            class="table-price-list"
            :items="items"
            :fields="manualFields"
            :hover="false"
            :striped="false"
            :bordered="true"
            :fixed="false"
            add-table-classes="mb-0"
          >
            <template #qty="{ item }">
              <td>
                <CInput disabled :value="item.qty" />
              </td>
            </template>
            <template #price="{ item, index }">
              <td>
                <PCurrencyInput
                  v-if="isEditableRow(index)"
                  :value="item.price"
                  :currency="currency"
                  :is-valid="isInvalidRow(index) ? false : null"
                  @input="(val) => onManualPriceChange(val, index)"
                  @change="(val) => onManualPriceChange(val, index)"
                />
                <PCurrencyInput
                  v-else
                  :value="item.price"
                  :currency="currency"
                  readonly
                  disabled
                />
              </td>
            </template>
          </CDataTable>

          <PAlert v-if="hasEmptyPrices" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'Every row needs a price. If you need fewer options, press the reset button to start over with a different number of photos.')
            }}
          </PAlert>
          <PAlert v-else-if="!isPricesValid" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'Prices must be increasing: each row must cost more than the previous one.')
            }}
          </PAlert>
          <PAlert v-else-if="!isPerPhotoValid" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'The per-photo price must decrease as quantity grows: each row must have a lower per-photo cost than the previous one.')
            }}
          </PAlert>
        </CCol>
      </CRow>


    </template>
  </div>
</template>

<script>

import PFRow from "@/domain/core/components/PFRow.vue";
import PCurrencyInput from "@/domain/core/components/PCurrencyInput.vue";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import {mapGetters} from "vuex";
import PAlert from "@/components/PAlert.vue";

export default {
  name: "PackPriceList",
  components: {PAlert, PSelectKeyValue, PCurrencyInput, PFRow},
  emits: ['update', 'mode-change'],
  props: {
    currency: {
      type: String,
      required: true
    },
    form_in_line: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mode: 'auto',
      items: [],
      configuration: {
        first_photo_price: null,
        complete_set_num: null,
        complete_set_price: null,
      }
    }
  },
  computed: {
    ...mapGetters('event', ['eventCurrency']),
    setNums() {
      const result = [];
      for (let i = 2; i <= 10; i++) {
        result.push({id: i, name: String(i)});
      }
      return result;
    },
    canApply() {
      const num = this.configuration.complete_set_num
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      return num !== null && num >= 2 && max > 0 && min > 0 && min < max && min > max / num
    },
    isFirstPhotoPriceTooLow() {
      const num = this.configuration.complete_set_num
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      return num !== null && num >= 2 && max > 0 && min > 0 && min < max && min <= max / num
    },
    isPricesValid() {
      if (this.items.length < 2) return true
      for (let i = 1; i < this.items.length; i++) {
        if (parseFloat(this.items[i].price) <= parseFloat(this.items[i - 1].price))
          return false
      }
      return true
    },
    isPerPhotoValid() {
      if (this.items.length < 2) return true
      for (let i = 1; i < this.items.length; i++) {
        const prev = parseFloat(this.items[i - 1].price) / this.items[i - 1].qty
        const curr = parseFloat(this.items[i].price) / this.items[i].qty
        if (curr >= prev) return false
      }
      return true
    },
    hasEmptyPrices() {
      return this.items.some(item => !item.price || parseFloat(item.price) <= 0)
    },
    manualFields() {
      return [
        {key: 'qty', label: this.$pgettext('priceList.pack.table', 'N° photos'), _style: 'width:90px;' },
        {key: 'price', label: this.$pgettext('priceList.pack.table', 'Price')},
      ]
    }
  },
  mounted() {
    this.emitModeChange()
  },
  watch: {
    canApply() {
      this.emitModeChange()
    }
  },
  methods: {
    emitModeChange() {
      this.$emit('mode-change', { mode: this.mode, canApply: this.canApply })
    },
    onSelectNum(option) {
      this.configuration.complete_set_num = option ? option.value : null
    },
    init({items, configuration}) {
      this.configuration = {
        first_photo_price: configuration.first_photo_price ?? null,
        complete_set_price: configuration.complete_set_price ?? null,
        complete_set_num: configuration.complete_set_num ?? null
      }
      this.items = items

      if (items.length > 0 && this.configuration.first_photo_price !== null) {
        this.mode = 'manual'
      } else if (items.length > 0 && this.configuration.complete_set_price !== null) {
        // Backward compatibility: old format without first_photo_price
        this.configuration.first_photo_price = items[0].price
        this.mode = 'manual'
      } else {
        this.mode = 'auto'
      }
      this.emitModeChange()
    },
    isEditableRow() {
      return true
    },
    isInvalidRow(index) {
      if (this.items.length < 2) return false
      const item = this.items[index]
      const price = parseFloat(item.price)
      if (!item.price || price <= 0) return true
      const perPhoto = price / item.qty
      if (index > 0) {
        const prev = this.items[index - 1]
        const prevPrice = parseFloat(prev.price)
        const prevPerPhoto = prevPrice / prev.qty
        if (price <= prevPrice) return true
        if (perPhoto >= prevPerPhoto) return true
      }
      if (index < this.items.length - 1) {
        const next = this.items[index + 1]
        const nextPrice = parseFloat(next.price)
        const nextPerPhoto = nextPrice / next.qty
        if (nextPrice <= price) return true
        if (nextPerPhoto >= perPhoto) return true
      }
      return false
    },
    isValid() {
      if (this.mode === 'auto') return this.canApply
      return this.items.length > 0 && !this.hasEmptyPrices && this.isPricesValid && this.isPerPhotoValid
    },
    getPriceList() {
      return {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      }
    },
    generateRows() {
      const num = parseInt(this.configuration.complete_set_num)
      const min = parseFloat(this.configuration.first_photo_price)
      const max = parseFloat(this.configuration.complete_set_price)

      if (!num || !min || !max || num < 1) return false

      if (num === 1) {
        this.items = [{qty: 1, price: max}]
        return true
      }

      // Costi marginali con serie geometrica decrescente: m_k = min * r^(k-1)
      // r viene calcolato affinché la somma dei marginali = max.
      // Questo garantisce sempre: prezzi totali crescenti e prezzo per-foto decrescente.
      const r = this.findDecayRate(min, max, num)

      let cumulative = 0
      this.items = []
      for (let i = 0; i < num; i++) {
        cumulative += min * Math.pow(r, i)
        const price = (i === num - 1) ? max : this.roundPrice(cumulative, max)
        this.items.push({qty: i + 1, price})
      }

      console.log('--- PackPriceList debug ---')
      console.log(`min=${min}, max=${max}, num=${num}, r=${r.toFixed(4)}`)
      this.items.forEach(item => {
        const pp = (item.price / item.qty).toFixed(2)
        console.log(`qty=${item.qty}  price=${item.price}  per-foto=${pp}`)
      })

      return true
    },
    findDecayRate(min, max, num) {
      const target = max / min
      let lo = 0, hi = 1
      for (let i = 0; i < 50; i++) {
        const mid = (lo + hi) / 2
        let sum = 0
        for (let k = 0; k < num; k++) sum += Math.pow(mid, k)
        if (sum < target) lo = mid
        else hi = mid
      }
      return (lo + hi) / 2
    },
    roundPrice(value, maxAmount) {
      const rounding = Math.pow(10, Math.floor(Math.log10(maxAmount / 100)))
      let roundedValue = Math.round(value / rounding)
      return parseFloat((roundedValue * rounding).toFixed(2))
    },
    onApply() {
      if (!this.canApply) return
      if (!this.generateRows()) return

      this.mode = 'manual'
      this.emitModeChange()
      this.emitUpdate()
    },
    onManualPriceChange(val, index) {
      this.$set(this.items[index], 'price', val)
      this.emitUpdate()
    },
    onResetToAuto() {
      this.items = []
      this.configuration = {
        first_photo_price: null,
        complete_set_num: null,
        complete_set_price: null,
      }
      this.mode = 'auto'
      this.emitModeChange()
      this.emitUpdate()
    },
    emitUpdate() {
      this.$emit('update', {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      })
    }
  }
}
</script>
